import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { fetcher } from 'lib/hooks/useFetch';
import { useOnScreen } from 'lib/hooks/useOnScreen';
import { ISafeFetchConfigOptions } from 'lib/requests/fetch';
import { ISearchResult } from 'lib/types';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import useSWRInfinite from 'swr/infinite';

export interface IUseInfiniteScrollOptions {
  shouldFetch?: boolean;
  queryParams?: Record<string, any>;
  allowFalseyQueryParamValues?: boolean;
  revalidateOnMount?: boolean;
}

export const useInfiniteScroll = <T extends unknown>(
  endpoint: string,
  pageSize: number,
  options: IUseInfiniteScrollOptions = {},
  configOptions?: ISafeFetchConfigOptions
) => {
  const [scrollRef, setScrollRef] = useState<HTMLDivElement>();
  const [total, setTotal] = useState<number>(0);

  const shouldFetch = options.shouldFetch ?? true;
  const queryParams = options.queryParams ?? {};
  const allowFalseyQueryParamValues =
    options.allowFalseyQueryParamValues ?? false;

  const { data, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite<ISearchResult<T>>(
      (pageIndex) => {
        if (shouldFetch) {
          const query = {
            ...queryParams,
            limit: pageSize,
            offset: pageIndex * pageSize,
          };
          return buildQueryParamLink(
            endpoint,
            query,
            allowFalseyQueryParamValues
          );
        }
        return null;
      },
      fetcher(configOptions),
      {
        initialSize: 1,
        revalidateFirstPage: false,
        revalidateOnMount: options.revalidateOnMount,
      }
    );

  const hasReachedEnd = !_.isEmpty(data)
    ? data[data.length - 1].results.length < pageSize
    : false;

  const shouldLoadMore =
    useOnScreen(scrollRef) && _.first(data)?.total > pageSize && !isValidating;

  useEffect(() => {
    if (!hasReachedEnd && shouldLoadMore) {
      setSize(size + 1);
    }
  }, [hasReachedEnd, shouldLoadMore]);

  // Persist total to reduce jitter between requests.
  if (data && !_.isEqual(_.first(data).total, total)) {
    setTotal(_.first(data).total);
  }

  return {
    data,
    isLoading,
    isValidating,
    mutate,
    total,
    scrollRef: useCallback((node: HTMLDivElement) => setScrollRef(node), []),
  };
};
